<template>
  <div
    :dir="locale === 'ar' ? 'rtl' : 'ltr'"
    :class="[
      locale === 'ar' ? 'font-arabic' : 'font-english',
      isDark ? 'dark' : ''
    ]"
  >
    <!-- Navbar -->
    <nav class="fixed top-0 left-0 right-0 z-50 overflow-visible transition-colors duration-300"
      :class="isDark
        ? 'bg-space-900/90 backdrop-blur-xl border-b border-galaxy-700/30'
        : 'bg-white/90 backdrop-blur-xl border-b border-primary-100/50 shadow-sm'
      "
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <div class="flex items-center gap-3 min-w-0 flex-1 md:flex-initial">
            <img src="@/assets/bugsy.png" alt="Bugsy" class="h-10 w-10 flex-shrink-0 object-contain drop-shadow-md transition-transform duration-300 hover:scale-110 hover:rotate-6" />
            <span class="text-xl font-bold galaxy-text-gradient truncate">{{ t('nav.brand') }}</span>
          </div>

          <div class="hidden md:flex items-center gap-6">
            <a v-for="link in navLinks" :key="link.href" :href="link.href"
              class="font-medium transition-colors duration-200"
              :class="isDark
                ? 'text-galaxy-200 hover:text-nebula-cyan'
                : 'text-gray-700 hover:text-primary-600'
              "
            >{{ t(link.label) }}</a>
          </div>

          <div class="header-actions relative z-[100] flex h-16 min-h-10 flex-shrink-0 items-center gap-2 self-stretch">
            <!-- Dark mode toggle: native button, always visible and clickable -->
            <button
              type="button"
              :aria-label="isDark ? 'Light mode' : 'Dark mode'"
              class="header-action-btn flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
              :class="isDark
                ? 'bg-white/15 text-amber-300 hover:bg-white/25 focus:ring-amber-400'
                : 'bg-violet-100 text-violet-700 hover:bg-violet-200 focus:ring-violet-500'"
              @click="toggleDarkMode"
            >
              <span class="material-icons text-2xl" aria-hidden="true">{{ isDark ? 'light_mode' : 'dark_mode' }}</span>
            </button>
            <!-- Language toggle: native button, always visible and clickable -->
            <button
              type="button"
              :aria-label="t('nav.languageAria')"
              :title="t('nav.language')"
              class="header-action-btn flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
              :class="isDark
                ? 'bg-white/15 text-cyan-300 hover:bg-white/25 focus:ring-cyan-400'
                : 'bg-violet-100 text-violet-700 hover:bg-violet-200 focus:ring-violet-500'"
              @click="toggleLanguage"
            >
              <span class="material-icons text-2xl" aria-hidden="true">translate</span>
            </button>
            <!-- Mobile menu: native button -->
            <button
              type="button"
              aria-label="Menu"
              class="header-action-btn md:hidden flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
              :class="isDark
                ? 'bg-white/15 text-cyan-300 hover:bg-white/25 focus:ring-cyan-400'
                : 'bg-violet-100 text-violet-700 hover:bg-violet-200 focus:ring-violet-500'"
              @click="mobileMenu = !mobileMenu"
            >
              <span class="material-icons text-2xl" aria-hidden="true">menu</span>
            </button>
          </div>
        </div>

        <!-- Mobile menu -->
        <div v-if="mobileMenu" class="md:hidden pb-4 space-y-1">
          <a v-for="link in navLinks" :key="link.href" :href="link.href"
            class="block px-3 py-2 rounded-lg transition-colors duration-200"
            :class="isDark
              ? 'text-galaxy-200 hover:text-nebula-cyan hover:bg-galaxy-800/50'
              : 'text-gray-700 hover:text-primary-600 hover:bg-primary-100/60'
            "
            @click="mobileMenu = false"
          >{{ t(link.label) }}</a>
        </div>
      </div>
    </nav>

    <main class="pt-16 transition-colors duration-300"
      :class="isDark ? 'bg-space-900' : 'bg-violet-50/40'"
    >
      <HeroSection :is-dark="isDark" />
      <AboutSection :is-dark="isDark" />
      <CoursesSection :is-dark="isDark" />
      <RegistrationForm :is-dark="isDark" />
    </main>

    <FooterSection :is-dark="isDark" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import HeroSection from '@/components/HeroSection.vue'
import AboutSection from '@/components/AboutSection.vue'
import CoursesSection from '@/components/CoursesSection.vue'
import RegistrationForm from '@/components/RegistrationForm.vue'
import FooterSection from '@/components/FooterSection.vue'

const { t, locale } = useI18n()
const mobileMenu = ref(false)
const isDark = ref(false)

const navLinks = [
  { href: '#home', label: 'nav.home' },
  { href: '#about', label: 'nav.about' },
  { href: '#courses', label: 'nav.courses' },
  { href: '#register', label: 'nav.register' },
]

onMounted(() => {
  const saved = localStorage.getItem('theme')
  if (saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDark.value = true
  }
  applyTheme()
  applyLocale()
})

watch(isDark, () => {
  applyTheme()
})

function applyTheme() {
  document.documentElement.classList.toggle('dark', isDark.value)
  document.body.classList.toggle('dark-body', isDark.value)
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
}

function toggleDarkMode() {
  isDark.value = !isDark.value
}

function applyLocale() {
  const dir = locale.value === 'ar' ? 'rtl' : 'ltr'
  document.body.dir = dir
  document.documentElement.lang = locale.value === 'ar' ? 'ar' : 'en'
}

function toggleLanguage() {
  locale.value = locale.value === 'en' ? 'ar' : 'en'
  localStorage.setItem('locale', locale.value)
  applyLocale()
}

watch(locale, () => {
  applyLocale()
})
</script>

<style scoped>
.header-actions {
  pointer-events: auto;
}
.header-action-btn {
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
}
.header-action-btn .material-icons {
  font-size: 1.5rem;
  user-select: none;
}
</style>
